import { Link } from 'react-router';

import type { FC, ReactNode } from 'react';

import { Icon } from 'icons';
import { cn } from '@/lib/utils';

import type { TNavItemProps } from './NavItem.props';

const NavItem: FC<TNavItemProps> = ({
  label,
  href,
  icon,
  count,
  isActive = false,
  isCollapsed = false,
  onClick,
  className,
}) => {
  const rowClassName = cn(
    'text-body h-nav-item rounded-md flex w-full items-center gap-2 px-2.5 font-sans transition-colors',
    'focus-visible:shadow-focus focus-visible:outline-none',
    isActive ? 'bg-accent-tint text-accent' : 'text-text-secondary hover:bg-accent-tint',
    isCollapsed && 'justify-center px-0',
    className,
  );

  const content: ReactNode = (
    <>
      {icon ? <Icon name={icon} aria-hidden className="shrink-0" /> : null}
      {isCollapsed && !icon ? (
        <span aria-hidden className="text-control-semibold">{label.charAt(0)}</span>
      ) : null}
      {isCollapsed ? null : (
        <>
          <span className="min-w-0 flex-1 truncate text-left">{label}</span>
          {count === undefined ? null : (
            <span className="text-caption-sm bg-accent text-text-inverse rounded-pill shrink-0 px-1.75 py-px">
              {count}
            </span>
          )}
        </>
      )}
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        aria-current={isActive ? 'page' : undefined}
        title={isCollapsed ? label : undefined}
        className={rowClassName}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={isCollapsed ? label : undefined}
      className={rowClassName}
    >
      {content}
    </button>
  );
};

export {
  NavItem,
};
